import type { MdocEngagementPayload } from '@/types/digital-credentials';

const DEFAULT_BASE_URL =
  'https://oneproof-dc-api.happyglacier-06873738.southcentralus.azurecontainerapps.io';

export function getOneProofDcApiBaseUrl(): string {
  const raw = import.meta.env.VITE_ONEPROOF_DCAPI_BASE_URL?.trim();
  const base = raw && raw.length > 0 ? raw : DEFAULT_BASE_URL;
  return base.replace(/\/$/, '');
}

/** Request body for POST /v1/engage_dcapi — boolean per field = request that element */
export type OneProofDataElements = {
  isoMain: Record<string, boolean>;
  isoAamva: Record<string, boolean>;
};

export type EngageDcApiRequest = {
  dataElements: OneProofDataElements;
};

export type EngageDcApiResponse = {
  sessionID: string;
  payload: MdocEngagementPayload;
};

export class OneProofDcApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly bodyText?: string
  ) {
    super(message);
    this.name = 'OneProofDcApiError';
  }
}

const DEFAULT_DATA_ELEMENTS: OneProofDataElements = {
  isoMain: {
    family_name: true,
    given_name: true,
    birth_date: true,
    document_number: true,
    portrait: true,
    // `issuing_jurisdiction` is rejected by OneProof in isoMain (400 invalid_data_elements).
    issuing_country: true,
  },
  isoAamva: {},
};

export async function engageDcApi(
  body: EngageDcApiRequest = { dataElements: DEFAULT_DATA_ELEMENTS }
): Promise<EngageDcApiResponse> {
  const base = getOneProofDcApiBaseUrl();
  const res = await fetch(`${base}/v1/engage_dcapi`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  const text = await res.text();
  if (!res.ok) {
    throw new OneProofDcApiError(
      `engage_dcapi failed (${res.status})`,
      res.status,
      text
    );
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(text) as unknown;
  } catch {
    throw new OneProofDcApiError('engage_dcapi returned invalid JSON', res.status, text);
  }

  const obj = parsed as Partial<EngageDcApiResponse>;
  if (
    !obj ||
    typeof obj.sessionID !== 'string' ||
    !obj.payload ||
    typeof obj.payload.deviceRequest !== 'string' ||
    typeof obj.payload.encryptionInfo !== 'string'
  ) {
    throw new OneProofDcApiError('engage_dcapi response missing sessionID or payload', res.status, text);
  }

  return obj as EngageDcApiResponse;
}

export async function submitDcApiResponse(
  sessionID: string,
  dcResponse: unknown
): Promise<unknown> {
  const base = getOneProofDcApiBaseUrl();
  const url = `${base}/v1/response_dcapi?sessionID=${encodeURIComponent(sessionID)}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dcResponse),
  });

  const text = await res.text();
  if (!res.ok) {
    throw new OneProofDcApiError(
      `response_dcapi failed (${res.status})`,
      res.status,
      text
    );
  }

  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export function serializeCredentialForJson(credential: Credential): unknown {
  const withJson = credential as Credential & { toJSON?: () => unknown };
  if (typeof withJson.toJSON === 'function') {
    return withJson.toJSON();
  }
  return {
    id: credential.id,
    type: credential.type,
  };
}

/**
 * Starts org-iso-mdoc presentation via the Digital Credentials API.
 * Must be invoked synchronously from a user activation (e.g. click/tap) — do not await other work in the same handler before calling this.
 */
export function requestMdocPresentation(
  engagement: EngageDcApiResponse,
  options?: { signal?: AbortSignal }
): Promise<Credential | null> {
  const creds = navigator.credentials;
  if (!creds?.get) {
    return Promise.reject(
      new Error('This browser does not support navigator.credentials.get')
    );
  }

  return creds.get({
    mediation: 'required',
    digital: {
      requests: [
        {
          protocol: 'org-iso-mdoc',
          data: engagement.payload,
        },
      ],
    },
    signal: options?.signal,
  }) as Promise<Credential | null>;
}

export async function submitMdocWalletResponse(
  sessionID: string,
  credential: Credential
): Promise<unknown> {
  return submitDcApiResponse(sessionID, serializeCredentialForJson(credential));
}

/**
 * When supported, asks the user agent whether org-iso-mdoc is allowed (Chrome).
 * Otherwise returns true if credentials.get exists (caller may still see runtime errors).
 */
export async function isOrgIsoMdocLikelySupported(): Promise<boolean> {
  const creds = navigator.credentials;
  if (!creds?.get) return false;
  if (typeof creds.userAgentAllowsProtocol === 'function') {
    try {
      return await creds.userAgentAllowsProtocol('org-iso-mdoc');
    } catch {
      return false;
    }
  }
  return true;
}
